/**
 * Tag.tsx - Individual tag component with hierarchical styling
 * 
 * CHANGE HISTORY:
 * - 251102_0637 [pending] danielbeer: Initial creation with Skinnr/Stylr integration
 * 
 * Styling Hierarchy:
 * 1. Overall group settings (highest priority)
 * 2. Parent group settings
 * 3. Individual tag settings (lowest priority)
 * 
 * Each level can specify: pattern, color, icon
 */

import React from 'react';
import { useSkinnr } from '@opcentral/dependr';
import type { Tag as TagType } from '../types';

export interface TagProps {
  tag: TagType | string;
  onClick?: () => void;
  onRemove?: () => void;
  size?: 'small' | 'medium' | 'large';
  className?: string;
  // Hierarchical styling
  groupStyle?: TagStyle;
  parentStyle?: TagStyle;
}

export interface TagStyle {
  pattern?: 'solid' | 'striped' | 'dotted' | 'gradient';
  color?: string;
  icon?: string | null;
}

export const Tag: React.FC<TagProps> = ({
  tag,
  onClick,
  onRemove,
  size = 'medium',
  className = '',
  groupStyle,
  parentStyle
}) => {
  const { currentSkin } = useSkinnr();

  // Normalize tag
  const tagData: TagType = typeof tag === 'string' 
    ? {
        id: tag,
        name: tag,
        displayName: tag,
        color: '#94a3b8',
        icon: null,
        group: null,
        description: '',
        metadata: { created: new Date(), updated: new Date(), usageCount: 0 },
        relationships: { parent: null, children: [], related: [] }
      }
    : tag;

  // Apply hierarchical styling (group > parent > tag)
  const finalStyle: TagStyle = {
    pattern: tagData.metadata?.pattern || parentStyle?.pattern || groupStyle?.pattern || 'solid',
    color: tagData.color || parentStyle?.color || groupStyle?.color || '#94a3b8',
    icon: tagData.icon ?? (parentStyle?.icon ?? groupStyle?.icon)
  };

  // Get pattern CSS class
  const patternClass = `pattern-${finalStyle.pattern}`;

  // Get size classes from Skinnr theme
  const sizeClasses = {
    small: 'text-xs px-2 py-0.5',
    medium: 'text-sm px-3 py-1',
    large: 'text-base px-4 py-1.5'
  };

  const baseClasses = `
    taggr-tag
    inline-flex items-center gap-1.5
    rounded-full
    font-medium
    transition-all duration-200
    ${sizeClasses[size]}
    ${patternClass}
    ${onClick ? 'cursor-pointer hover:opacity-80' : ''}
    ${className}
  `.trim().replace(/\s+/g, ' ');

  return (
    <span
      className={baseClasses}
      style={{
        backgroundColor: finalStyle.color,
        color: getContrastColor(finalStyle.color!),
        ...getPatternStyles(finalStyle.pattern!, finalStyle.color!)
      }}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={(e) => {
        if (onClick && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onClick();
        }
      }}
      data-skinnr-theme={(currentSkin as any)?.name}
    >
      {/* Icon */}
      {finalStyle.icon && (
        <span className="taggr-tag-icon" aria-hidden="true">
          {finalStyle.icon}
        </span>
      )}

      {/* Tag name */}
      <span className="taggr-tag-text">
        {tagData.displayName}
      </span>

      {/* Remove button */}
      {onRemove && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          className="taggr-tag-remove ml-1 hover:opacity-70 transition-opacity"
          aria-label={`Remove ${tagData.displayName}`}
        >
          ×
        </button>
      )}
    </span>
  );
};

/**
 * Get contrasting text color for background
 */
function getContrastColor(bgColor: string): string {
  // Convert hex to RGB
  const hex = bgColor.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);

  // Calculate luminance
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

  return luminance > 0.5 ? '#000000' : '#ffffff';
}

/**
 * Get pattern-specific styles
 */
function getPatternStyles(pattern: string, baseColor: string): React.CSSProperties {
  switch (pattern) {
    case 'striped':
      return {
        backgroundImage: `repeating-linear-gradient(
          45deg,
          ${baseColor},
          ${baseColor} 10px,
          ${adjustColor(baseColor, -10)} 10px,
          ${adjustColor(baseColor, -10)} 20px
        )`
      };
    
    case 'dotted':
      return {
        backgroundImage: `radial-gradient(circle, ${adjustColor(baseColor, -20)} 1px, transparent 1px)`,
        backgroundSize: '8px 8px'
      };
    
    case 'gradient':
      return {
        backgroundImage: `linear-gradient(135deg, ${baseColor} 0%, ${adjustColor(baseColor, 20)} 100%)`
      };
    
    case 'solid':
    default:
      return {};
  }
}

/**
 * Adjust color brightness
 */
function adjustColor(color: string, amount: number): string {
  const hex = color.replace('#', '');
  const r = Math.max(0, Math.min(255, parseInt(hex.substr(0, 2), 16) + amount));
  const g = Math.max(0, Math.min(255, parseInt(hex.substr(2, 2), 16) + amount));
  const b = Math.max(0, Math.min(255, parseInt(hex.substr(4, 2), 16) + amount));
  
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}
